import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
    Component,
    DoCheck,
    ElementRef,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    Optional,
    Self,
    ViewChild
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
    AbstractControl,
    ControlValueAccessor,
    NgControl,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {
    MAT_FORM_FIELD,
    MatFormField,
    MatFormFieldControl,
    MatFormFieldModule
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';

/**
 * @example <caption>Example usage in Material form</caption>
 *          <mat-form-field appearance="outline">
 *              <mat-label>Phone number</mat-label>
 *              <app-phone-input
 *                  formControlName="tel"
 *                  #phoneInput
 *              ></app-phone-input>
 *              <mat-icon matSuffix>phone</mat-icon>
 *              <mat-error *ngIf="phoneInput.errorState">
 *                  Invalid phone number
 *              </mat-error>
 *              <mat-hint>Include area code</mat-hint>
 *          </mat-form-field>
 */
@Component({
    selector: 'app-phone-input',
    templateUrl: './phone-input.component.html',
    styleUrls: ['./phone-input.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: PhoneInputComponent }
    ],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        FlexLayoutModule,
        MatInputModule
    ]
})
export class PhoneInputComponent
    implements
        ControlValueAccessor,
        MatFormFieldControl<string>,
        OnDestroy,
        DoCheck
{
    static nextId = 0;

    private _placeholder!: string;
    private _required!: boolean;
    private _disabled!: boolean;
    private _showExtension!: boolean;

    @ViewChild('area') areaInput!: HTMLInputElement;
    @ViewChild('exchange') exchangeInput!: HTMLInputElement;
    @ViewChild('subscriber') subscriberInput!: HTMLInputElement;
    @ViewChild('extension') extensionInput!: HTMLInputElement;

    @HostBinding('class.example-floating')
    get shouldLabelFloat(): boolean {
        return this.focused || !this.empty;
    }

    @HostBinding('id') id = `example-tel-input-${PhoneInputComponent.nextId++}`;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('aria-describedby') userAriaDescribedBy: string | undefined;

    get placeholder(): string {
        return this._placeholder;
    }

    @Input()
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    get required(): boolean {
        return this._required;
    }

    @Input()
    set required(value: boolean) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    get disabled(): boolean {
        return this._disabled;
    }

    @Input()
    set disabled(value: boolean) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.parts.disable() : this.parts.enable();
        this.stateChanges.next();
    }

    get showExtension(): boolean {
        return this._showExtension;
    }

    @Input()
    set showExtension(value: boolean) {
        this._showExtension = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    get value(): string | null {
        if (this.parts.valid) {
            const {
                value: { area, exchange, subscriber, extension }
            } = this.parts;
            return area + exchange + subscriber + extension;
        }
        return null;
    }

    @Input()
    set value(input: string | null) {
        let parts = { area: '', exchange: '', subscriber: '', extension: '' };
        if (input?.length && input?.length >= 10) {
            parts.area = input.substr(0, 3);
            parts.exchange = input.substr(3, 3);
            parts.subscriber = input.substr(6, 4);
            if (input.length > 10) parts.extension = input.substr(10, 6);
        }
        this.parts.setValue(parts);
        this.stateChanges.next();
    }

    get errorState(): boolean {
        let result = this.touched && this.parts.invalid;
        if (result && !this.required && this.empty) result = false;
        return result;
    }

    hasError(errorCode: string): boolean {
        if (errorCode === 'required' && !this._required) return false;
        let result = false;
        Object.keys(this.parts.controls).forEach((key) => {
            if (this.parts.controls[key].hasError(errorCode)) result = true;
        });
        return result;
    }

    parts: UntypedFormGroup;
    stateChanges = new Subject<void>();
    focused = false;
    touched = false;
    controlType = 'example-tel-input';
    onChange = (_: any) => {};
    onTouched = () => {};

    get empty(): boolean {
        const {
            value: { area, exchange, subscriber, extension }
        } = this.parts;
        return !area && !exchange && !subscriber && !extension;
    }

    constructor(
        formBuilder: UntypedFormBuilder,
        private _focusMonitor: FocusMonitor,
        private _elementRef: ElementRef<HTMLElement>,
        @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
        @Optional() @Self() public ngControl: NgControl
    ) {
        this.parts = formBuilder.group({
            area: [
                null,
                [Validators.required, Validators.pattern(/^(\d\d\d)$/)]
            ],
            exchange: [
                null,
                [Validators.required, Validators.pattern(/^(\d\d\d)$/)]
            ],
            subscriber: [
                null,
                [Validators.required, Validators.pattern(/^(\d\d\d\d)$/)]
            ],
            extension: [null, [Validators.pattern(/^(\d{0,6})$/)]]
        });

        if (this.ngControl !== null) {
            this.ngControl.valueAccessor = this;
        }
    }

    ngOnDestroy(): void {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    ngDoCheck(): void {
        if (this.ngControl?.control?.touched) {
            this.touched = true;
            this.parts.markAllAsTouched();
            this.onTouched();
            this.stateChanges.next();
        }
    }

    onFocusIn(event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (
            !this._elementRef.nativeElement.contains(
                event.relatedTarget as Element
            )
        ) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    autoFocusNext(
        control: AbstractControl,
        nextElement?: HTMLInputElement
    ): void {
        if (!control.errors && nextElement) {
            this._focusMonitor.focusVia(nextElement, 'program');
        }
    }

    autoFocusPrev(
        control: AbstractControl,
        prevElement: HTMLInputElement
    ): void {
        if (control.value.length < 1) {
            this._focusMonitor.focusVia(prevElement, 'program');
        }
    }

    setDescribedByIds(ids: string[]) {
        const controlElement = this._elementRef.nativeElement.querySelector(
            '.example-tel-input-container'
        )!;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick() {
        let input: HTMLElement = this.parts.controls.subscriber.valid
            ? this.extensionInput
            : this.parts.controls.exchange.valid
            ? this.subscriberInput
            : this.parts.controls.area.valid
            ? this.exchangeInput
            : this.areaInput;
        this._focusMonitor.focusVia(input, 'program');
    }

    writeValue(input: string | null): void {
        this.value = input;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    _handleInput(
        control: AbstractControl,
        nextElement?: HTMLInputElement
    ): void {
        this.autoFocusNext(control, nextElement);
        this.onChange(this.value);
    }

    static ngAcceptInputType_disabled: BooleanInput;
    static ngAcceptInputType_required: BooleanInput;
}
